import clsx from 'clsx';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { getBookings } from '../apis/booking';
import BookingCard from '../components/BookingCard';
import { BookingCardProps } from '../components/BookingCard/index.types';
import EmptyPage from '../components/EmptyPage';
import FullScreenError from '../components/Error/FullScreen';
import Header from '../components/Header';
import { Event } from '../enums/event';
import { recordEvent } from '../utils/analytics';
import PageLoader from '../components/Loader/PageLoader';

const Skeleton = () => (
    <div className='p-10 shadow-100 rounded-20'>
        <div className='animate-pulse flex gap-10 w-full'>
            <div className='w-80 h-100 rounded-10 bg-skeleton' />
            <div className='flex flex-col justify-between overflow-hidden'>
                <div className='flex flex-col gap-5'>
                    <div className='inline-flex items-center font-normal w-fit bg-neutral text-neutral-800 py-0 px-[2px] rounded-10 min-h-[20px] !bg-transparent'>
                        <p className='body-xs px-[3px] text-skeleton bg-skeleton rounded-5'>Flight to Mumbai</p>
                    </div>
                    <div className='text-skeleton bg-skeleton rounded-10'>
                        <p className='body-lg flex items-center gap-5 font-medium'>
                            <span className='whitespace-nowrap overflow-hidden text-ellipsis'>DEL</span>
                            <span className='whitespace-nowrap overflow-hidden text-ellipsis'>BOM</span>
                        </p>
                        <p className='body-sm overflow-hidden whitespace-nowrap text-ellipsis'>
                            Sat, 24 Feb • IndiGo 6E449
                        </p>
                    </div>
                </div>
                <div className='inline-flex items-center font-normal w-fit bg-skeleton text-skeleton py-0 px-[2px] rounded-10 min-h-[20px]'>
                    <p className='body-xs px-[3px]'>Booking Complete</p>
                </div>
            </div>
        </div>
    </div>
);

const Bookings = () => {
    const [data, setData] = useState<BookingCardProps['bookingData'][]>([]);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pageLoading, setPageLoading] = useState(false);

    const fetchBookings = useCallback(async (retry: boolean = false) => {
        try {
            if (retry) setError(false);
            setLoading(true);

            const trips = await getBookings();
            setData(trips);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBookings();
    }, [fetchBookings]);

    if (pageLoading) return <PageLoader />;

    const renderContent = () => {
        if (loading)
            return (
                <div className='flex flex-col p-20 gap-15 overflow-hidden'>
                    {Array.from({ length: 10 }).map((_, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Skeleton key={index} />
                    ))}
                </div>
            );

        if (error)
            return (
                <div className='my-auto'>
                    <FullScreenError />
                </div>
            );

        if (!data.length)
            return (
                <EmptyPage
                    heading='No bookings available'
                    subHeading='You have not made any bookings yet'
                    showBackBtn
                />
            );

        return (
            <div className='flex flex-col p-20 gap-15'>
                {data.map((trip) => (
                    <Fragment key={trip.bookingId}>
                        <div className='p-10 shadow-100 rounded-20'>
                            <BookingCard
                                bookingData={trip}
                                onClick={() => {
                                    setPageLoading(true);
                                    recordEvent(Event.HC_TRIP_OPEN, {
                                        bookingParams: trip,
                                    });
                                    window.location.href = `${window.location.origin}/support/booking/${trip.bookingId}?entryPoint=webview`;
                                }}
                            />
                        </div>
                    </Fragment>
                ))}
            </div>
        );
    };

    return (
        <div className={clsx('flex flex-col', loading ? 'h-full overflow-hidden' : 'min-h-full')}>
            <Header navTitle='Bookings' />
            {renderContent()}
        </div>
    );
};

export default Bookings;
